import { useState } from "react";
import { DataGrid, GridRowSelectionModel } from "@mui/x-data-grid";
import { Box, Modal, Button } from "@mui/material";
import { MeteringPoint } from "api/models/MeteringPoint";
import dayjs from "dayjs";
import MeteringPointContracts from "components/Modals/MeteringPointContracts";
import MeteringPointActiveChip from "components/MeteringPointActiveChip";

interface MeasurementPointsProps {
  clientId: string;
  meteringPoints: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: MeteringPoint[];
  };
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  setSnackbar: any;
}

const MeasurementPoints = ({
  meteringPoints,
  rowSelectionModel,
  setRowSelectionModel,
  paginationModel,
  setPaginationModel,
  setSnackbar,
}: MeasurementPointsProps) => {
  const [contractsModal, setContractsModal] = useState(null);

  const columns = [
    {
      field: "EIC",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "Address",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "Municipality",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "Active grid agreements",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "Active supply agreements",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "NPM valid agreements",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "Valid operator",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Last sync",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Status",
      width: 100,
      sortable: false,
      renderCell: (params: any) => (
        <MeteringPointActiveChip isActive={params.row.isActive} />
      ),
    },
    {
      field: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setContractsModal(params.row.meteringPointId)}
          >
            See contracts
          </Button>
        );
      },
    },
  ];

  const contractPeriod = (startDate: string, endDate?: string) => {
    return endDate ? `${startDate} - ${endDate}` : `${startDate} - ...`;
  };

  const rows = meteringPoints?.content.map((meteringPoint) => {
    const activeContract = meteringPoint.contracts.find(
      (contract) => contract.isActive,
    );

    return {
      id: activeContract ? activeContract.id : meteringPoint.id,
      meteringPointId: meteringPoint.id,
      EIC: meteringPoint.eic,
      Address: meteringPoint.address,
      Municipality: meteringPoint.city,
      "Active grid agreements": meteringPoint.gridAgreementFirstDate
        ? `${dayjs(meteringPoint.gridAgreementFirstDate).format("DD.MM.YYYY")} - ...`
        : null,
      "Active supply agreements": meteringPoint.supplyAgreementFirstDate
        ? contractPeriod(
            dayjs(meteringPoint.supplyAgreementFirstDate).format("DD.MM.YYYY"),
            meteringPoint.supplyAgreementLastDate
              ? dayjs(meteringPoint.supplyAgreementLastDate).format(
                  "DD.MM.YYYY",
                )
              : "...",
          )
        : null,
      "NPM valid agreements": activeContract
        ? contractPeriod(
            activeContract.firstDate
              ? dayjs(activeContract.firstDate).format("DD.MM.YYYY")
              : "",
            activeContract.lastDate
              ? dayjs(activeContract.lastDate).format("DD.MM.YYYY")
              : "",
          )
        : null,
      "Valid operator": meteringPoint.operatorEic,
      "Last sync": meteringPoint.lastReadingsSyncedAt
        ? dayjs(meteringPoint.lastReadingsSyncedAt).format("DD.MM.YYYY HH:mm")
        : null,
      isActive: meteringPoint.isActive,
    };
  });

  return (
    <Box sx={{ mt: "2rem" }}>
      <DataGrid
        checkboxSelection
        onRowSelectionModelChange={(
          newRowSelectionModel: GridRowSelectionModel,
        ) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        columns={columns}
        rows={rows}
        rowCount={meteringPoints.totalElements}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="server"
        pageSizeOptions={[15, 25, 50, 100]}
      />
      <Modal open={!!contractsModal} onClose={() => setContractsModal(null)}>
        <MeteringPointContracts id={contractsModal} setSnackbar={setSnackbar} />
      </Modal>
    </Box>
  );
};

export default MeasurementPoints;
